import React, { FunctionComponent, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import {
  HeaderConfig,
  ManageEntitiesContentLayout
} from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { useSpecificParams } from 'record-it-components/hooks/useSpecificParams/useSpecificParams';
import { TimeTrackingActivity } from '../../../classes/entities/TimeTrackingActivity/timeTrackingActivityTypes';
import { useEmptyCreateEntityButtonConfig } from '../../../hooks/useEmptyCreateEntityButtonConfig';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { TimeTrackingActivityList } from '../../timeTrackingActivity/TimeTrackingActivityList/TimeTrackingActivityList';
import { useConfigureTimeTrackingActivityAdminPageBreadcrumbs } from './useConfigureTimeTrackingActivityAdminPageBreadcrumbs';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useEntityByIdOrOriginalId } from '../../../hooks/entity/useEntityByIdOrOriginalId';

export type TimeTrackingActivityAdminPageProps = PropsWithoutChildren<
  Record<never, never>
>;

export const TimeTrackingActivityAdminPage: FunctionComponent<TimeTrackingActivityAdminPageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const { parentActivityId, timeTrackingActivityCompilationId } =
      useSpecificParams({
        requiredParamNames: ['timeTrackingActivityCompilationId'],
        optionalParamNames: ['parentActivityId']
      });
    const compilation = useEntityByIdOrOriginalId(
      EntityName.TimeTrackingActivityCompilation,
      timeTrackingActivityCompilationId
    );
    assertNotNullOrUndefined(
      compilation,
      `no compilation found for "${timeTrackingActivityCompilationId}"`
    );

    useConfigureTimeTrackingActivityAdminPageBreadcrumbs(
      compilation,
      parentActivityId
    );

    const activities = useEntities(
      EntityName.TimeTrackingActivity,
      'getByCompilationIdAndParentActivityId',
      compilation.id,
      parentActivityId ?? null
    );
    const timeTrackingActivityRepository = useEntityRepository(
      EntityName.TimeTrackingActivity
    );

    const handleCreateEntity = useEventCallback(() => {
      const maxOrder = Math.max(...activities.map((a) => a.order), 0);
      timeTrackingActivityRepository.create({
        order: maxOrder + 1,
        ownerUserGroupId: compilation.ownerUserGroupId,
        parentActivityId: parentActivityId ?? null,
        compilationId: compilation.id
      });
    });

    const createEntityButtonConfig = useEmptyCreateEntityButtonConfig({
      onCreateEntity: handleCreateEntity,
      text: t('components.page.TimeTrackingActivityAdminPage.createEntity')
    });

    const headerConfig: HeaderConfig = useMemo(() => {
      return {
        iconConfig:
          iconConfigurations.prefixed.page.TimeTrackingActivityAdminPage
            .titleIcon,
        title: t('components.page.TimeTrackingActivityAdminPage.pageTitle')
      };
    }, [t]);

    const renderEntities = useCallback(
      (currentPageActivities: Array<TimeTrackingActivity>) => {
        return (
          <TimeTrackingActivityList
            timeTrackingActivities={currentPageActivities}
            data-testid="TimeTrackingActivityAdminPage.ActivityList"
          ></TimeTrackingActivityList>
        );
      },
      []
    );

    return (
      <ManageEntitiesContentLayout<TimeTrackingActivity, never>
        createEntityButtonConfig={createEntityButtonConfig}
        headerConfig={headerConfig}
        entities={activities}
        renderEntities={renderEntities}
        usePagination={false}
      ></ManageEntitiesContentLayout>
    );
  });
